import { displayDigits } from '../utils/displayDigits';
import React from 'react';
import { useLongPress } from '../hooks/useLongPress';
import { WorkoutTypeIconTile } from './WorkoutTypeIconTile';

export interface NmDayWorkoutItem {
  id: string;
  sport: string;
  title: string;
  typeId?: number | null;
  sportEnglishName?: string | null;
  metrics?: string[];
  done?: boolean;
  accentColor?: string;
  structurePreview?: React.ReactNode;
  onPress?: () => void;
  onLongPress?: () => void;
}

interface Props {
  items: NmDayWorkoutItem[];
  showDate?: boolean;
  date?: string;
  summary?: string;
  className?: string;
}

/** Single workout row with optional long-press handler. */
function WorkoutRow({ item }: { item: NmDayWorkoutItem }) {
  const { bind, wrapClick } = useLongPress(() => item.onLongPress?.(), {
    disabled: !item.onLongPress,
  });

  return (
    <button
      key={item.id}
      type="button"
      className="nm-day-workouts__row"
      onClick={wrapClick(item.onPress)}
      {...(item.onLongPress ? bind : {})}
    >
      <WorkoutTypeIconTile
        typeId={item.typeId}
        name={item.sportEnglishName ?? item.sport}
        size="sm"
        className="nm-day-workouts__icon"
      />
      <span className="nm-day-workouts__body">
        <span className="nm-day-workouts__title">{displayDigits(item.title)}</span>
        {item.metrics?.length ? (
          <span className="nm-day-workouts__metrics">
            {item.metrics.map((metric, index) => (
              <React.Fragment key={`${item.id}-metric-${index}`}>
                {index > 0 ? <span className="nm-day-workouts__metric-dot" aria-hidden /> : null}
                <span className="nm-day-workouts__metric ltr-island">{displayDigits(metric)}</span>
              </React.Fragment>
            ))}
          </span>
        ) : null}
        {item.structurePreview ? (
          <span className="nm-day-workouts__structure">{item.structurePreview}</span>
        ) : null}
      </span>
      <span className={`nm-day-workouts__action${item.done ? ' nm-day-workouts__action--done' : ''}`}>
        {item.done ? '✓' : '›'}
      </span>
    </button>
  );
}

/** Web port of Vo2DayWorkoutListRtl — day workout rows in calendar. */
export function NmDayWorkoutList({ items, showDate = false, date, summary, className = '' }: Props) {
  return (
    <div className={`nm-day-workouts ${className}`.trim()} dir="rtl">
      {(showDate || summary) && (
        <div className="nm-day-workouts__header">
          {showDate && date ? <span className="nm-day-workouts__date">{date}</span> : null}
          {summary ? <span className="nm-day-workouts__summary">{summary}</span> : null}
        </div>
      )}
      <div className="nm-day-workouts__list">
        {items.map((item) => (
          <WorkoutRow key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}
